import { AlertCircle, ChevronDown, HelpCircle } from "lucide-react";
import { useState, type ReactNode } from "react";

import { SectionHeader } from "components/SectionHeader";
import { EmptyState, LoadingCards } from "components/states";
import { SECTION_TITLES } from "../../copy/section-titles";
import { Card, CardContent } from "components/ui/card";

import { LinkifiedText } from "../notifications/LinkifiedText";

import type { InformationItem } from "./schemas";
import { useInformation } from "./use-event-content";

export function InformationSection({ eventId }: { eventId: string }) {
  const information = useInformation(eventId);

  const count = information.status === "success" ? information.data.length : 0;
  const head = (
    <SectionHeader
      className="mb-6"
      kicker={SECTION_TITLES.info.kicker}
      title={SECTION_TITLES.info.title}
      subline={
        count > 0
          ? `${count} ${count === 1 ? "question" : "questions"}, answered.`
          : undefined
      }
    />
  );

  if (information.status === "loading")
    return (
      <Frame>
        {head}
        <LoadingCards cards={3} />
      </Frame>
    );
  if (information.status === "error")
    return (
      <Frame>
        {head}
        <EmptyState icon={AlertCircle} title="Could not load information">
          {information.error}
        </EmptyState>
      </Frame>
    );
  if (information.data.length === 0)
    return (
      <Frame>
        {head}
        <EmptyState icon={HelpCircle} title="No information yet">
          Questions and answers will appear here once published.
        </EmptyState>
      </Frame>
    );

  return (
    <Frame>
      {head}
      <div className="space-y-3" data-testid="information-content">
        {information.data.map((item) => (
          <InfoCard key={item.id} item={item} />
        ))}
      </div>
    </Frame>
  );
}

/** The tab's column, capped on a desktop so a question stays easy to scan. */
function Frame({ children }: { children: ReactNode }) {
  return <div className="mx-auto w-full max-w-3xl">{children}</div>;
}

/**
 * One question, with its answer folded away until it is asked for.
 *
 * Items stored under the retired INFORMATION kind render the same way. They
 * are the handful written before the section became questions only, and
 * showing them as a question beats hiding rows an organiser can still see and
 * delete in the admin tab.
 */
function InfoCard({ item }: { item: InformationItem }) {
  const [open, setOpen] = useState(false);
  return (
    <Card
      className="rounded-[22px] border-border bg-card p-0"
      data-card="question"
    >
      <CardContent className="p-0">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          className="flex min-h-14 w-full items-center justify-between gap-3 rounded-[22px] px-3.5 py-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
          data-testid={`information-question-${item.id}`}
        >
          <span className="font-display text-[1.1875rem] leading-[1.2] text-foreground">
            {item.title}
          </span>
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${
              open ? "rotate-180" : ""
            }`}
          />
        </button>
        {open && item.body && (
          <p
            className="whitespace-pre-line px-3.5 pb-3.5 text-sm text-muted-foreground"
            data-testid={`information-answer-${item.id}`}
          >
            <LinkifiedText text={item.body} />
          </p>
        )}
      </CardContent>
    </Card>
  );
}
